import React, { Fragment, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Footer from "./Footer/index";
import TapTop from "./TapTop/index";
import Header from "./Header";
import SideBarLayout from "./SideBar-Layout";
import Themecustomizer from "./ThemeCustomizer";

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 68;
const NAVBAR_HEIGHT = 64;
const MOBILE_BREAKPOINT = 992;

export const SidebarContext = React.createContext({
  collapsed: false,
  setCollapsed: () => {},
  mobileOpen: false,
  setMobileOpen: () => {},
  isMobile: false,
});

const Layout = () => {
  const location = useLocation();

  const [width, setWidth] = useState(window.innerWidth);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const overlayRef = useRef(null);

  const isMobile = width < MOBILE_BREAKPOINT;

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Close mobile sidebar on route change — deferred to avoid sync setState in effect */
  useEffect(() => {
    const t = setTimeout(() => setMobileOpen(false), 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar_collapsed", String(next));
  };

  const sidebarWidth = isMobile
    ? SIDEBAR_EXPANDED
    : collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const contentMargin = isMobile ? 0 : sidebarWidth;

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, mobileOpen, setMobileOpen, toggleCollapsed, isMobile }}>
      <Fragment>
        <TapTop />

        {/* ── Navbar ── */}
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0,
          height: NAVBAR_HEIGHT,
          background: "var(--navbar-bg)",
          borderBottom: "1px solid var(--navbar-border)",
          backdropFilter: "blur(20px) saturate(180%)",
          zIndex: 1000,
          transition: "background 0.3s",
        }}>
          <Header />
        </div>

        {/* ── Mobile overlay ── */}
        {isMobile && mobileOpen && (
          <div
            ref={overlayRef}
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 998,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(2px)",
              animation: "fadeIn 0.2s ease",
            }}
            aria-hidden="true"
          />
        )}

        {/* ── Sidebar ── */}
        <aside style={{
          position: "fixed",
          top: NAVBAR_HEIGHT,
          left: 0,
          width: sidebarWidth,
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          background: "var(--sidebar-bg)",
          borderRight: "1px solid var(--sidebar-border)",
          backdropFilter: "blur(20px) saturate(180%)",
          overflowY: "auto",
          overflowX: "visible",
          zIndex: 999,
          transition: "width 0.28s cubic-bezier(0.4,0,0.2,1), transform 0.28s cubic-bezier(0.4,0,0.2,1)",
          transform: isMobile
            ? mobileOpen ? "translateX(0)" : "translateX(-100%)"
            : "translateX(0)",
          display: "flex",
          flexDirection: "column",
        }}>
          <SideBarLayout />
        </aside>

        {/* ── Main content + footer ── */}
        <div style={{
          marginLeft: contentMargin,
          marginTop: NAVBAR_HEIGHT,
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          transition: "margin-left 0.28s cubic-bezier(0.4,0,0.2,1)",
          display: "flex",
          flexDirection: "column",
        }}>
          <main style={{ flex: 1, padding: "24px 20px" }}>
            <Outlet />
          </main>

          <footer style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            padding: "10px 24px",
            background: "var(--navbar-bg)",
            borderTop: "1px solid var(--navbar-border)",
          }}>
            <Footer />
          </footer>
        </div>

        <Themecustomizer />
      </Fragment>
    </SidebarContext.Provider>
  );
};

export default Layout;
